import React from 'react';
import {
  Box, Button, Chip, CircularProgress, Collapse, IconButton, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import type { PromptDetail, PromptRow } from '../../types/usage';
import { formatTokens, formatUsd } from '../../services/usage.service';

interface Props {
  rows: PromptRow[]; showUser: boolean; expandedId: string | null; onToggle: (id: string) => void;
  detail: PromptDetail | undefined; detailLoading: boolean; hasMore: boolean; onLoadMore: () => void; isLoading: boolean;
}

const statusColor = (s: PromptRow['status']): 'default' | 'error' | 'warning' =>
  s === 'failed' ? 'error' : s === 'interrupted' ? 'warning' : 'default';

export const PromptsTable: React.FC<Props> = ({ rows, showUser, expandedId, onToggle, detail, detailLoading, hasMore, onLoadMore, isLoading }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell />
          <TableCell>Time</TableCell>
          <TableCell>Prompt</TableCell>
          <TableCell>Session</TableCell>
          {showUser && <TableCell>User</TableCell>}
          <TableCell>Model(s)</TableCell>
          <TableCell align="right">Tokens</TableCell>
          <TableCell align="right">Cost</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => {
          const open = expandedId === r.prompt_group_id;
          const cols = showUser ? 9 : 8;
          return (
            <React.Fragment key={r.prompt_group_id}>
              <TableRow hover>
                <TableCell>
                  <IconButton size="small" aria-label={`${open ? 'collapse' : 'expand'} ${r.prompt_group_id}`} onClick={() => onToggle(r.prompt_group_id)}>
                    {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </IconButton>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(r.occurred_at).toLocaleString()}</TableCell>
                <TableCell sx={{ maxWidth: 360 }}><Typography variant="body2" noWrap title={r.prompt_preview || ''}>{r.prompt_preview || '—'}</Typography></TableCell>
                <TableCell>
                  {r.session_id ? <Link component={RouterLink} to={`/?session=${r.session_id}`}>{r.session_title || r.session_id}</Link>
                    : <Typography variant="body2" color="text.secondary">(deleted session)</Typography>}
                </TableCell>
                {showUser && <TableCell>{r.user_id}</TableCell>}
                <TableCell>{r.models.join(', ')}</TableCell>
                <TableCell align="right">{r.legacy ? <Chip size="small" label="cost only" /> : formatTokens(r.tokens.total)}</TableCell>
                <TableCell align="right">{formatUsd(r.cost_usd)}</TableCell>
                <TableCell><Chip size="small" label={r.status} color={statusColor(r.status)} variant={r.status === 'ok' ? 'outlined' : 'filled'} /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={cols} sx={{ p: 0, border: 0 }}>
                  <Collapse in={open} unmountOnExit>
                    <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                      {detailLoading || !detail ? <CircularProgress size={18} /> : (
                        <Table size="small">
                          <TableHead><TableRow>
                            <TableCell>Model</TableCell><TableCell>Provider</TableCell><TableCell>Source</TableCell>
                            <TableCell align="right">Input</TableCell><TableCell align="right">Output</TableCell>
                            <TableCell align="right">Cache read</TableCell><TableCell align="right">Cache write</TableCell>
                            <TableCell align="right">Cost</TableCell><TableCell>Turns</TableCell>
                          </TableRow></TableHead>
                          <TableBody>
                            {detail.models.map((m, i) => (
                              <TableRow key={i}>
                                <TableCell>{m.model}</TableCell>
                                <TableCell>{m.provider || '—'}</TableCell>
                                <TableCell>{m.source}</TableCell>
                                <TableCell align="right">{formatTokens(m.input_tokens)}</TableCell>
                                <TableCell align="right">{formatTokens(m.output_tokens)}</TableCell>
                                <TableCell align="right">{formatTokens(m.cache_read_tokens)}</TableCell>
                                <TableCell align="right">{formatTokens(m.cache_creation_tokens)}</TableCell>
                                <TableCell align="right">{formatUsd(m.cost_usd)}</TableCell>
                                <TableCell>{m.num_turns != null ? `${m.num_turns} turns · ${((m.duration_ms || 0) / 1000).toFixed(1)}s` : '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          );
        })}
        {rows.length === 0 && !isLoading && (
          <TableRow><TableCell colSpan={showUser ? 9 : 8}><Typography color="text.secondary" align="center" sx={{ py: 3 }}>No prompts in this range.</Typography></TableCell></TableRow>
        )}
      </TableBody>
    </Table>
    {hasMore && (
      <Box sx={{ textAlign: 'center', py: 2 }}><Button onClick={onLoadMore} disabled={isLoading}>Load more</Button></Box>
    )}
  </Box>
);

export default PromptsTable;
